import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { companyInfo } from '../data/mock';
import { services } from '../data/services';
import { Menu, X, Phone, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const servicesRef = useRef(null);
  const location = useLocation();

  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (servicesRef.current && !servicesRef.current.contains(event.target)) {
        setIsServicesOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsServicesOpen(false);
  }, [location]);

  const scrollToSection = (href) => {
    setIsMobileMenuOpen(false);
    if (href.startsWith('#')) {
      if (!isHomePage) {
        window.location.href = '/' + href;
        return;
      }
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const headerBg = isScrolled || !isHomePage;
  const textColor = headerBg ? 'text-gray-700' : 'text-white';
  const logoColor = headerBg ? 'text-[#1a365d]' : 'text-white';

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        headerBg
          ? 'bg-white shadow-lg py-2'
          : 'bg-transparent py-4'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center cursor-pointer">
            <div className={`flex flex-col items-center ${logoColor}`}>
              <div className="border-t-2 border-current w-full mb-1"></div>
              <span className="text-xl sm:text-2xl font-bold tracking-tight leading-none text-center">RENEWED</span>
              <span className="text-sm sm:text-base font-medium tracking-wider leading-none text-center">ENVIRONMENTAL</span>
              <div className="border-b-2 border-current w-full mt-1"></div>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8">
            <Link
              to="/"
              className={`font-medium transition-colors hover:text-blue-400 ${textColor}`}
            >
              Home
            </Link>

            {/* Services Dropdown */}
            <div className="relative" ref={servicesRef}>
              <button
                onClick={() => setIsServicesOpen(!isServicesOpen)}
                className={`flex items-center space-x-1 font-medium transition-colors hover:text-blue-400 ${textColor}`}
              >
                <span>Services</span>
                <ChevronDown className={`h-4 w-4 transition-transform ${isServicesOpen ? 'rotate-180' : ''}`} />
              </button>

              {isServicesOpen && (
                <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-xl shadow-xl border border-gray-100 py-2 animate-in fade-in slide-in-from-top-2 duration-200">
                  <Link
                    to="/services"
                    className="block px-4 py-3 text-[#1a365d] font-semibold hover:bg-gray-50 transition-colors border-b border-gray-100"
                  >
                    All Services
                  </Link>
                  {services.map((service) => (
                    <Link
                      key={service.id}
                      to={`/services/${service.slug}`}
                      className="block px-4 py-2.5 text-gray-600 hover:bg-gray-50 hover:text-[#1a365d] transition-colors"
                    >
                      {service.title}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <button
              onClick={() => scrollToSection('#bins')}
              className={`font-medium transition-colors hover:text-blue-400 ${textColor}`}
            >
              Pricing
            </button>
            <button
              onClick={() => scrollToSection('#areas')}
              className={`font-medium transition-colors hover:text-blue-400 ${textColor}`}
            >
              Areas
            </button>
            <button
              onClick={() => scrollToSection('#faq')}
              className={`font-medium transition-colors hover:text-blue-400 ${textColor}`}
            >
              FAQ
            </button>
            <button
              onClick={() => scrollToSection('#contact')}
              className={`font-medium transition-colors hover:text-blue-400 ${textColor}`}
            >
              Contact
            </button>
          </nav>

          {/* CTA Buttons */}
          <div className="hidden md:flex items-center space-x-4">
            <a
              href={`tel:${companyInfo.phone}`}
              className={`flex items-center space-x-2 font-medium transition-colors ${logoColor}`}
            >
              <Phone className="h-4 w-4" />
              <span>{companyInfo.phone}</span>
            </a>
            <Button
              onClick={() => scrollToSection('#bins')}
              className="bg-[#1a365d] hover:bg-[#2d4a7c] text-white font-semibold px-6 py-2 rounded-md transition-all duration-200 hover:scale-105"
            >
              Get Quote
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? (
              <X className={`h-6 w-6 ${logoColor}`} />
            ) : (
              <Menu className={`h-6 w-6 ${logoColor}`} />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden absolute top-full left-0 right-0 bg-white shadow-lg py-4 px-4 animate-in slide-in-from-top duration-200">
            <nav className="flex flex-col space-y-2">
              <Link
                to="/"
                className="text-gray-700 font-medium py-3 px-4 rounded-lg hover:bg-gray-50"
              >
                Home
              </Link>
              
              {/* Mobile Services Section */}
              <div className="border-t border-gray-100 pt-2">
                <Link
                  to="/services"
                  className="text-[#1a365d] font-semibold py-3 px-4 rounded-lg hover:bg-gray-50 block"
                >
                  All Services
                </Link>
                {services.map((service) => (
                  <Link
                    key={service.id}
                    to={`/services/${service.slug}`}
                    className="text-gray-600 py-2 px-8 rounded-lg hover:bg-gray-50 block text-sm"
                  >
                    {service.title}
                  </Link>
                ))}
              </div>

              <div className="border-t border-gray-100 pt-2">
                <button
                  onClick={() => scrollToSection('#bins')}
                  className="text-gray-700 font-medium text-left py-3 px-4 rounded-lg hover:bg-gray-50 w-full"
                >
                  Pricing
                </button>
                <button
                  onClick={() => scrollToSection('#areas')}
                  className="text-gray-700 font-medium text-left py-3 px-4 rounded-lg hover:bg-gray-50 w-full"
                >
                  Service Areas
                </button>
                <button
                  onClick={() => scrollToSection('#faq')}
                  className="text-gray-700 font-medium text-left py-3 px-4 rounded-lg hover:bg-gray-50 w-full"
                >
                  FAQ
                </button>
              </div>

              <div className="border-t border-gray-100 pt-4 mt-2">
                <a
                  href={`tel:${companyInfo.phone}`}
                  className="flex items-center space-x-2 text-[#1a365d] font-medium py-3 px-4"
                >
                  <Phone className="h-4 w-4" />
                  <span>{companyInfo.phone}</span>
                </a>
                <Button
                  onClick={() => scrollToSection('#bins')}
                  className="bg-[#1a365d] hover:bg-[#2d4a7c] text-white font-semibold w-full mt-2"
                >
                  Get Quote
                </Button>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
